import { FCC } from "@dgs/core";
import { createContext, useCallback, useContext, useState } from "react";
import { ContactViewIndexResource } from "../../shared/types";
import { useGlobalContactView } from "../global/GlobalContactViewProvider";

interface LocalContactProviderState {
	localContactView: ContactViewIndexResource | null;
	setLocalContactView: (contactView: ContactViewIndexResource | null) => void;
}

const LocalContactViewContext = createContext<LocalContactProviderState | undefined>(undefined);

const getCurrentContactView = (contactViews: ContactViewIndexResource[], key: string) => {
	const id = localStorage.getItem(key);

	if (!id) {
		return null;
	}

	const parsedId = Number(id);
	return contactViews.find((contactView) => contactView.id === parsedId) || contactViews[0];
};

interface Props {
	contactViewKey: string;
}

export const LocalContactViewProvider: FCC<Props> = ({ children, contactViewKey }) => {
	const { contactViews } = useGlobalContactView();
	const [localContactView, _setContactView] = useState<ContactViewIndexResource | null>(
		getCurrentContactView(contactViews, contactViewKey),
	);

	const setLocalContactView = useCallback(
		(contactView: ContactViewIndexResource | null) => {
			contactView ? localStorage.setItem(contactViewKey, `${contactView.id}`) : localStorage.removeItem(contactViewKey);
			_setContactView(contactView);
		},
		[contactViewKey],
	);

	return (
		<LocalContactViewContext.Provider value={{ localContactView, setLocalContactView }}>
			{children}
		</LocalContactViewContext.Provider>
	);
};

const setLocalContactView = () => {
	throw new Error("useLocalContactView should be wrapped in a LocalContactViewProvider");
};

export const useLocalContactView = () => {
	const ctx = useContext(LocalContactViewContext);

	return ctx ? ctx : { setLocalContactView, localContactView: null };
};
